import React from 'react';
import { useTranslation } from 'react-i18next';
import { renderWhatsApp } from '../../../../utils/whatsappFormat';
import { cn } from '../../../../utils/cn';

/**
 * Burbuja de un WhatsApp saliente con el formato de WhatsApp (*negrita*, _cursiva_,
 * ~tachado~, ```código```). `renderWhatsApp` arma JSX por tokens, sin HTML: el texto (que
 * trae datos del comercio y texto libre del operador) no puede inyectar markup.
 *
 * @param {{ text: string, className?: string }} props
 */
export function WhatsappMessageBubble({ text, className }) {
  return (
    <div className={cn('rounded-lg border border-border bg-bg-subtle p-3', className)}>
      <div className="ml-auto max-w-[85%] whitespace-pre-wrap break-words rounded-lg rounded-br-md bg-brand-500 px-3 py-2 text-sm text-white">
        {renderWhatsApp(text, { format: true })}
      </div>
    </div>
  );
}

/**
 * Correo tal como lo arma billing: el asunto como texto y el HTML completo (diseño fijo con
 * logo, tabla de notas y botón) en un iframe aislado.
 *
 * `sandbox=""` sin excepciones: el documento no ejecuta scripts, no envía formularios, no
 * abre ventanas y queda en un origen opaco (sin acceso a la sesión ni al DOM de la app).
 * NUNCA sumar `allow-scripts` junto con `allow-same-origin`: juntos anulan el aislamiento.
 * Por el mismo aislamiento el alto no se puede ajustar al contenido: es fijo, con scroll.
 *
 * @param {{ subject?: string|null, html: string, className?: string, frameClassName?: string }} props
 */
export function EmailMessagePreview({ subject, html, className, frameClassName }) {
  const { t } = useTranslation();
  return (
    <div className={cn('space-y-2', className)}>
      <div className="rounded-md border border-border bg-bg px-3 py-2 text-sm">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-fg-muted">{t('Asunto')}</span>
        {/* Texto del operador y del comercio: va como texto, nunca por t(). */}
        <p className="m-0 mt-0.5 break-words font-medium text-fg">{subject || '—'}</p>
      </div>
      <iframe
        title={t('Vista previa del correo')}
        sandbox=""
        referrerPolicy="no-referrer"
        srcDoc={html}
        className={cn('block h-[480px] w-full rounded-md border border-border bg-white', frameClassName)}
      />
    </div>
  );
}

/**
 * Mensaje de un recordatorio ya armado por billing, según el canal: burbuja de WhatsApp o
 * correo (asunto + HTML aislado). Lo usan la vista previa del editor de plantillas y la de
 * la plantilla del sistema; sirve igual para cualquier mensaje que devuelva billing.
 *
 * @param {{ channel: 'email'|'whatsapp', subject?: string|null, html?: string, text?: string,
 *   className?: string }} props
 */
export default function ReminderMessagePreview({ channel, subject, html, text, className }) {
  if (channel === 'email') {
    return <EmailMessagePreview subject={subject} html={html ?? ''} className={className} />;
  }
  return <WhatsappMessageBubble text={text ?? ''} className={className} />;
}
